import React, { useState, useEffect } from 'react';
import './Episodes.css';
import EpisodeCard from '../components/EpisodeCard';
import { EpisodeService } from '../services/EpisodeService';
import { Episode } from '../types/types';
import MobileEpisodeCard from '../components/mobile/MobileEpisodeCard';
import backgroundImage from '../assets/basketball-bg.jpg';
import { Helmet } from 'react-helmet-async';

const Episodes: React.FC = () => {
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [visibleEpisodes, setVisibleEpisodes] = useState(3);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        setLoading(true);
        const data = await EpisodeService.getAllEpisodes();
        setEpisodes(data);
      } catch (err) {
        console.error('Error fetching episodes:', err);
        setError('Failed to load episodes');
      } finally {
        setLoading(false);
      }
    };
    
    fetchEpisodes();
  }, []);

  const sortedEpisodes = [...episodes].sort((a, b) => {
    // Sort by date (newest first)
    return new Date(b.date).getTime() - new Date(a.date).getTime();
  });

  const displayedEpisodes = sortedEpisodes.slice(0, visibleEpisodes);
  const hasMoreEpisodes = visibleEpisodes < sortedEpisodes.length;

  const handleLoadMore = () => {
    setVisibleEpisodes(prev => prev + 3);
  };
  
  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>Loading episodes...</p>
      </div>
    );
  }
  
  if (error) {
    return <div className="error">{error}</div>;
  }
  
  if (episodes.length === 0) {
    return <div className="no-episodes">No episodes available</div>;
  }
  
  return (
    <div className="episodes-page">
      <Helmet>
        <title>All Episodes - Basketball Culture Podcast</title>
        <meta name="description" content="Watch all episodes of Basketball Culture Podcast featuring interviews with basketball legends, coaches, and cultural icons." />
        <meta name="keywords" content="basketball podcast episodes, basketball interviews, basketball culture" />
        <link rel="canonical" href="https://basketballculturepodcast.com/episodes" />
      </Helmet>

      <div 
        className="episodes-hero"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${backgroundImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="episodes-title">EPISODES</h1>
          </div>
        </div>
      </div>

      <div className="episodes-content">
        <div className="episodes-container">
          <div className="episodes-grid">
            {displayedEpisodes.map((episode) => (
              isMobile ? (
                <MobileEpisodeCard key={episode.id} episode={episode} />
              ) : (
                <EpisodeCard key={episode.id} episode={episode} />
              )
            ))}
          </div>

          {hasMoreEpisodes && (
            <div className="load-more-container">
              <button className="load-more-button" onClick={handleLoadMore}>
                Load More Episodes
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Episodes; 